// Entry point: bootstrap data, mount Preact App.
import './styles/global.css';
import { render } from 'preact';
import { batch } from '@preact/signals';
import { api, UnauthorizedError } from './api';
import { BRAND } from './brand';
import { loadStartupData, type StartupState } from './startup';
import { me, currentUserId, groups, settingsOpen, chatLoading, isElevatedUser } from './state';
import { App } from './components/App';
import { initNotif, shouldShowIosInstallHint } from './notify';
import { initSound } from './sound';
import { initInstall } from './install';
import { initBadge } from './badge';
import { initAppearance } from './appearance-state';
import { showToast } from './components/Toast';
import { applyPanelClasses } from './panels';
import { applyHash, parseHash } from './hash';
import { router } from './router';
import { installLivenessHandlers, startSyncPoll } from './actions';
import { observeTableScrollEdges } from './table-scroll';
import { fitVisualViewport } from './viewport-fit';
import type { Group } from './types';

interface MeResponse { displayName?: string; userId: string; isElevated?: boolean }
interface GroupsResponse { groups: Group[] }

function StartupScreen({ state }: { state: StartupState }) {
  const title = {
    loading: `Loading ${BRAND.name}...`,
    redirecting: 'Redirecting to sign in...',
    error: `Could not load ${BRAND.name}`,
  }[state.kind];
  return (
    <div class="startup-screen" role="status" aria-live="polite" aria-busy={state.kind !== 'error'}>
      <p class="startup-title">{title}</p>
      {state.kind === 'error'
        ? <>
            <p class="startup-error" role="alert">{state.message}</p>
            <p>Check your connection and try again.</p>
            <button type="button" onClick={state.retry}>Try again</button>
          </>
        : <p>Please wait.</p>}
    </div>
  );
}

function sortGroups(list: Group[]): Group[] {
  return list.slice().sort((a, b) => {
    const parseTs = (s: string | undefined): number => {
      if (!s) return 0;
      const norm = s.includes('T') ? s : s.replace(' ', 'T') + 'Z';
      return Date.parse(norm) || 0;
    };
    const ta = parseTs(a.lastActivityAt);
    const tb = parseTs(b.lastActivityAt);
    if (tb !== ta) return tb - ta;
    return a.name.localeCompare(b.name);
  });
}

function setupViewportFit(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  fitVisualViewport(vv, document.documentElement.style);
  document.addEventListener('focusin', (ev: FocusEvent) => {
    const t = ev.target as HTMLElement | null;
    if (t?.id === 'chat-input') {
      setTimeout(() => {
        try { (t as HTMLElement).scrollIntoView({ block: 'end', behavior: 'smooth' }); } catch { /* ignore */ }
      }, 250);
    }
  });
}

const IOS_HINT_DISMISSED_KEY = 'nanoclaw:ios-install-hint-dismissed';

function maybeShowIosInstallHint(): void {
  if (!shouldShowIosInstallHint()) return;
  try {
    if (localStorage.getItem(IOS_HINT_DISMISSED_KEY) === '1') return;
  } catch { /* ignore */ }
  const el = document.createElement('div');
  el.setAttribute('role', 'note');
  el.style.cssText =
    'position:fixed;left:12px;right:12px;bottom:12px;z-index:9999;' +
    'background:var(--install-hint-bg);color:var(--install-hint-fg);border:1px solid var(--install-hint-border);border-radius:var(--radius-xl);' +
    'padding:12px 14px;font:13px var(--font-ui);-webkit-font-smoothing:antialiased;' +
    'box-shadow:0 4px 12px var(--install-hint-shadow);display:flex;gap:10px;align-items:flex-start';
  el.innerHTML =
    '<div style="flex:1">Add to Home Screen to receive notifications when the app is closed. ' +
    'Tap the Share button, then "Add to Home Screen".</div>' +
    '<button type="button" aria-label="Dismiss" ' +
    'style="background:transparent;color:var(--install-hint-muted);border:0;font-size:18px;line-height:1;cursor:pointer;padding:0 4px">×</button>';
  const btn = el.querySelector('button');
  if (btn) {
    btn.addEventListener('click', () => {
      try { localStorage.setItem(IOS_HINT_DISMISSED_KEY, '1'); } catch { /* ignore */ }
      el.remove();
    });
  }
  document.body.appendChild(el);
}

async function init(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) throw new Error('Chat app root is missing');
  initAppearance((message) => showToast(message, 'err'));
  initNotif();
  initSound();
  initInstall();
  initBadge();
  setupViewportFit();
  installLivenessHandlers();
  applyPanelClasses();
  maybeShowIosInstallHint();
  const [meRes, groupsRes] = await loadStartupData(
    (signal) => Promise.all([
      api<MeResponse>('api/me', { signal }),
      api<GroupsResponse>('api/groups', { signal }),
    ]),
    (state) => render(<StartupScreen state={state} />, app),
  );
  batch(() => {
    me.value = meRes.displayName || meRes.userId;
    currentUserId.value = meRes.userId;
    isElevatedUser.value = meRes.isElevated === true;
    groups.value = sortGroups(groupsRes.groups);
  });
  const parsed = parseHash();
  if (parsed && parsed.groupId) chatLoading.value = true;
  render(<App />, app);
  observeTableScrollEdges(app);
  await applyHash(router, { newConversation: true }).catch((err) => console.error('initial route failed', err));
  startSyncPoll();
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('settings') === '1') {
      settingsOpen.value = true;
      sp.delete('settings');
      const q = sp.toString();
      const url = window.location.pathname + (q ? '?' + q : '') + window.location.hash;
      window.history.replaceState(window.history.state, '', url);
    }
    handleShareTarget();
  } catch { /* ignore */ }
}

// Web Share Target: if the user shared from another app, the manifest's
// share_target landing route (/ui/chat/share) preserves the title/text/url
// query params. Stuff them into the composer once it mounts, then rewrite
// the URL so a reload doesn't re-share.
function handleShareTarget(): void {
  const onShareRoute = window.location.pathname.endsWith('/share');
  const sp = new URLSearchParams(window.location.search);
  const title = sp.get('title') || '';
  const text = sp.get('text') || '';
  const url = sp.get('url') || '';
  if (!title && !text && !url) {
    // Even if we landed on /share with no params, normalize back to /.
    if (onShareRoute) window.history.replaceState(window.history.state, '', '/ui/chat/' + window.location.hash);
    return;
  }
  const combined = [title, text, url].filter(Boolean).join('\n').trim();
  const apply = (): boolean => {
    const el = document.getElementById('chat-input') as HTMLTextAreaElement | null;
    if (!el) return false;
    el.value = (el.value ? el.value + '\n\n' : '') + combined;
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.focus();
    return true;
  };
  if (!apply()) {
    let tries = 0;
    const t = window.setInterval(() => {
      if (apply() || ++tries > 50) window.clearInterval(t);
    }, 100);
  }
  const nextPath = onShareRoute ? '/ui/chat/' : window.location.pathname;
  window.history.replaceState(window.history.state, '', nextPath + window.location.hash);
}

init().catch((err) => {
  if (err instanceof UnauthorizedError) return;
  console.error('App initialization failed', err);
  const app = document.getElementById('app');
  if (app) render(<StartupScreen state={{
    kind: 'error',
    message: err instanceof Error ? err.message : 'An unexpected startup error occurred.',
    retry: () => window.location.reload(),
  }} />, app);
});
